import { useRef, useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import { Check, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";
import { CalendarEventType, EventPeriod } from "services/eventCalendarQuery";
import { COLORS, FONT_FAMILY } from "constants/variables";
import {
  ArrowButton, DateControl, FilterRow, MonthGrid, PeriodOption,
  PeriodPicker, PickerYear, SelectButton, TypeSelect,
} from "./EventPeriodFilterStyled";

interface Props {
  value: EventPeriod;
  onChange: (period: EventPeriod) => void;
  eventType: CalendarEventType;
  onEventTypeChange: (type: CalendarEventType) => void;
}

const EventPeriodFilter = ({ value, onChange, eventType, onEventTypeChange }: Props) => {
  const { t, i18n } = useTranslation();
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [typeAnchor, setTypeAnchor] = useState<HTMLElement>(null);
  const [picker, setPicker] = useState<{ anchor: HTMLElement; year: number }>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const label = (key: string) => t(`view.newsAndMedia.events.filters.${key}`);
  const months = Array.from({ length: 12 }, (_, month) =>
    new Date(2024, month, 1).toLocaleString(i18n.language, { month: "long" })
  );
  const types: { id: CalendarEventType; label: string }[] = [
    { id: "all", label: label("allEvents") },
    { id: "DSU", label: label("DsuEvents") },
    { id: "partner", label: label("partnerEvents") },
  ];
  const selectedType = types.find((type) => type.id === eventType)?.label;
  const periodLabel = value.scope === "all" ? label("allTime")
    : value.scope === "year" ? t("view.newsAndMedia.events.filters.wholeYear", { year: value.year })
    : `${months[value.month]} ${value.year}`;
  const compactPeriodLabel = value.scope === "month"
    ? new Date(value.year, value.month, 1).toLocaleString(i18n.language, { month: "short", year: "numeric" })
    : value.scope === "year" ? String(value.year) : periodLabel;

  const navigatePeriod = (increment: number) => {
    if (value.scope === "year") onChange({ ...value, year: value.year + increment });
    else {
      const date = new Date(value.year, value.month + increment, 1);
      onChange({ ...value, year: date.getFullYear(), month: date.getMonth() });
    }
  };
  const choosePeriod = (period: EventPeriod) => {
    onChange(period);
    setPicker(null);
  };

  return (
    <FilterRow $biggerCursor={biggerCursor}>
      <TypeSelect>
        <SelectButton type="button" aria-label={`${label("eventType")}: ${selectedType}`}
          aria-haspopup="menu" aria-expanded={Boolean(typeAnchor)}
          onClick={(event) => setTypeAnchor(event.currentTarget)}>
          <span>{isMobile ? label(eventType === "all" ? "allTypesShort" : eventType === "DSU" ? "dsuTypesShort" : "partnerTypesShort") : selectedType}</span><ChevronDown size={14} />
        </SelectButton>
      </TypeSelect>
      <DateControl>
        {value.scope !== "all" && !isMobile && (
          <ArrowButton type="button" aria-label={label(value.scope === "month" ? "previousMonth" : "previousYear")}
            onClick={() => navigatePeriod(-1)}><ChevronLeft size={14} /></ArrowButton>
        )}
        <SelectButton type="button" aria-label={`${label("period")}: ${periodLabel}`}
          aria-haspopup="dialog" aria-expanded={Boolean(picker)}
          onClick={(event) => setPicker({ anchor: event.currentTarget, year: value.year })}>
          <span>{isMobile ? compactPeriodLabel : periodLabel}</span><ChevronDown size={14} />
        </SelectButton>
        {value.scope !== "all" && !isMobile && (
          <ArrowButton type="button" aria-label={label(value.scope === "month" ? "nextMonth" : "nextYear")}
            onClick={() => navigatePeriod(1)}><ChevronRight size={14} /></ArrowButton>
        )}
      </DateControl>
      <Menu anchorEl={typeAnchor} open={Boolean(typeAnchor)} onClose={() => setTypeAnchor(null)}
        PaperProps={{ sx: { borderRadius: "16px", backgroundColor: COLORS.Cultured, mt: 1 } }}
        MenuListProps={{ "aria-label": label("eventType") }}>
        {types.map((type) => (
          <MenuItem key={type.id} selected={type.id === eventType}
            sx={{ fontFamily: FONT_FAMILY.poppins, fontSize: 14, minHeight: 44, color: COLORS.RaisinBlack,
              "&.Mui-selected": { backgroundColor: COLORS.Background, color: COLORS.Crimson } }}
            onClick={() => { onEventTypeChange(type.id); setTypeAnchor(null); }}>{type.label}</MenuItem>
        ))}
      </Menu>
      <Popover open={Boolean(picker)} anchorEl={picker?.anchor} onClose={() => setPicker(null)}
        TransitionProps={{ onEntered: () => pickerRef.current?.querySelector<HTMLButtonElement>('button[aria-pressed="true"]')?.focus() }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { borderRadius: "16px", backgroundColor: COLORS.Cultured, mt: 1 } }}>
        {picker && (
          <PeriodPicker ref={pickerRef} role="dialog" aria-label={label("period")}>
            <PickerYear>
              <ArrowButton type="button" aria-label={label("previousYear")}
                onClick={() => setPicker({ ...picker, year: picker.year - 1 })}><ChevronLeft size={16} /></ArrowButton>
              <span>{picker.year}</span>
              <ArrowButton type="button" aria-label={label("nextYear")}
                onClick={() => setPicker({ ...picker, year: picker.year + 1 })}><ChevronRight size={16} /></ArrowButton>
            </PickerYear>
            <PeriodOption type="button" $selected={value.scope === "all"} aria-pressed={value.scope === "all"}
              autoFocus={value.scope === "all"}
              onClick={() => choosePeriod({ ...value, scope: "all" })}>
              {label("allTime")}{value.scope === "all" && <Check size={16} />}
            </PeriodOption>
            <PeriodOption type="button" $selected={value.scope === "year" && value.year === picker.year}
              aria-pressed={value.scope === "year" && value.year === picker.year}
              autoFocus={value.scope === "year" && value.year === picker.year}
              onClick={() => choosePeriod({ ...value, scope: "year", year: picker.year })}>
              {t("view.newsAndMedia.events.filters.wholeYear", { year: picker.year })}
              {value.scope === "year" && value.year === picker.year && <Check size={16} />}
            </PeriodOption>
            <MonthGrid role="group" aria-label={label("month")}>
              {months.map((month, index) => (
                <PeriodOption key={month} type="button"
                  $selected={value.scope === "month" && value.year === picker.year && value.month === index}
                  aria-pressed={value.scope === "month" && value.year === picker.year && value.month === index}
                  autoFocus={value.scope === "month" && value.year === picker.year && value.month === index}
                  onClick={() => choosePeriod({ scope: "month", year: picker.year, month: index })}>{month}</PeriodOption>
              ))}
            </MonthGrid>
          </PeriodPicker>
        )}
      </Popover>
    </FilterRow>
  );
};

export default EventPeriodFilter;
